import { getLinkedinCompanies } from 'API/company-urn';
import { LinkedinAccount, LinkedinAccountStatuses } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import { TFunction } from 'i18next';
import _ from 'lodash';
import React from 'react';
import { NavigationStates } from 'stores/app-bar-navigation-store';

interface BlacklistDialogState {
  selectedLinkedinAccount: string;
  companyQuery: string;
  companyOptions: string[][];
  selectedCompanies: string[][];
  companyIsEmpty: boolean;
  companyLoading: boolean;
}

interface Params {
  t: TFunction;
  language: string;
  setActiveTab: (tab: NavigationStates) => void;
  addLocalBlacklistDialog: BlacklistDialogState;
  setAddLocalBlacklistDialog: React.Dispatch<React.SetStateAction<any>>;
  linkedinAccounts: LinkedinAccount[];
  setLinkedinAccounts: React.Dispatch<React.SetStateAction<LinkedinAccount[]>>;
  handleOpenErrorDialog: (error: any, endpoint: string, method: string) => void;
}

const useBlacklistCompanyLookup = ({
  t,
  language,
  setActiveTab,
  addLocalBlacklistDialog,
  setAddLocalBlacklistDialog,
  linkedinAccounts,
  setLinkedinAccounts,
  handleOpenErrorDialog,
}: Params) => {
  const handleBlacklistLinkedinAccountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddLocalBlacklistDialog((prev: any) => ({
      ...prev,
      selectedLinkedinAccount: event.target.value,
      companyQuery: '',
      companyOptions: [],
      selectedCompanies: [],
      companyIsEmpty: false,
    }));
  };

  const handleBlacklistCompanyQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddLocalBlacklistDialog((prev: any) => ({
      ...prev,
      companyQuery: event.target.value,
      companyIsEmpty: false,
    }));
  };

  const promptReloginForCompanyLookup = () => {
    const shouldOpenAccountSync = window.confirm(
      t('Your LinkedIn account session is not active. Do you want to open LinkedIn Account Management and login again?')
    );
    if (shouldOpenAccountSync) {
      setActiveTab(NavigationStates.LINKEDIN_ACCOUNT_MANAGEMENT);
    }
  };

  const handleSearchBlacklistCompanies = async () => {
    const { companyQuery, selectedLinkedinAccount } = addLocalBlacklistDialog;
    if (!selectedLinkedinAccount || companyQuery.trim() === '') {
      setAddLocalBlacklistDialog((prev: any) => ({ ...prev, companyOptions: [] }));
      return;
    }

    const selectedAccount = linkedinAccounts.find((account) => account._id === selectedLinkedinAccount);
    if (!selectedAccount || selectedAccount.status !== LinkedinAccountStatuses.SYNCHRONIZED) {
      setAddLocalBlacklistDialog((prev: any) => ({
        ...prev,
        companyOptions: [],
        companyIsEmpty: false,
      }));
      promptReloginForCompanyLookup();
      return;
    }

    setAddLocalBlacklistDialog((prev: any) => ({ ...prev, companyLoading: true }));
    const res = await getLinkedinCompanies(companyQuery, language, selectedLinkedinAccount);

    if (!res.error) {
      const options = Array.isArray(res) ? res : [];
      setAddLocalBlacklistDialog((prev: any) => ({
        ...prev,
        companyOptions: options,
        companyIsEmpty: options.length === 0,
      }));
    } else {
      setAddLocalBlacklistDialog((prev: any) => ({ ...prev, companyOptions: [] }));
      if ((res as any)?.error?.code === 'SESSION_INVALID') {
        setLinkedinAccounts((prev) =>
          prev.map((account) =>
            account._id === selectedLinkedinAccount ? { ...account, status: LinkedinAccountStatuses.NOT_SYNCHRONIZED } : account
          )
        );
        promptReloginForCompanyLookup();
        setAddLocalBlacklistDialog((prev: any) => ({ ...prev, companyLoading: false }));
        return;
      }
      handleOpenErrorDialog(res?.error, '/linkedin/lookups/company', 'post');
    }

    setAddLocalBlacklistDialog((prev: any) => ({ ...prev, companyLoading: false }));
  };

  const handleToggleBlacklistCompany = (value: string) => {
    return () => {
      setAddLocalBlacklistDialog((prev: any) => {
        const selected = _.cloneDeep(prev.selectedCompanies);
        const selectedUrns = selected.map((item: string[]) => item[0]);

        if (selectedUrns.includes(value)) {
          _.remove(selected, (item: string[]) => item[0] === value);
        } else {
          const option = prev.companyOptions.find((item: string[]) => item[0] === value);
          if (!_.isNil(option)) {
            selected.push(option);
          }
        }

        return { ...prev, selectedCompanies: selected };
      });
    };
  };

  const handleRemoveBlacklistCompany = (value: string) => {
    return () => {
      setAddLocalBlacklistDialog((prev: any) => {
        const selected = _.cloneDeep(prev.selectedCompanies);
        _.remove(selected, (item: string[]) => item[0] === value);
        return { ...prev, selectedCompanies: selected };
      });
    };
  };

  return {
    handleBlacklistLinkedinAccountChange,
    handleBlacklistCompanyQueryChange,
    handleSearchBlacklistCompanies,
    handleToggleBlacklistCompany,
    handleRemoveBlacklistCompany,
  };
};

export default useBlacklistCompanyLookup;
